/**
 * A bill's verdict and headline figures, in one place.
 *
 * Two views of the same figures (2026-10-07): the internal screens show the
 * whole bill with pass-throughs added back (`expectedTotal` / `invoiceTotal`);
 * the client report shows the compared charges only (`compared`, `billReport`).
 * Unmapped charges count in the compared figures; only charge-map
 * pass-throughs are added back.
 *
 * Every screen used to decide "overcharge" by comparing whole totals: the
 * expected invoice's total against the retailer's. The retailer's total also
 * carries charges we never validate — unmapped lines, environmental charges on a
 * site without GreenPower, charges switched off by hand, and the GST on all of
 * them — so a bill whose every compared charge matched still read as an
 * overcharge of exactly those amounts. NMI 60014106913 Jul 2026: compared TOTAL
 * -$0.09 "Matched", Bill Run header "OVERCHARGE +$287.09".
 *
 * Agreed 2026-10-02 (Bill Send Plan): charges we cannot validate are
 * pass-through and are added back at the bill's own amounts. So:
 *
 *   variance       = compared retailer − compared expected  (× 1.1 when GST is counted)
 *   invoice total  = the retailer's total as printed
 *   expected total = invoice total − variance
 *
 * which is the whole bill on both sides, differing only by what we validate.
 * The verdict is `classifyVariance` on those two, the same rule the client
 * report always used (≤ $0.50, or ≥ 95% accurate, is within range).
 *
 * The compared figures come from `reportComparison`, which the Comparison
 * table, the report screen and the server's email all use, so the verdict
 * cannot disagree with the table it summarises. Pure and sync: pass the
 * charge map in when it is loaded; without it lines pair on name alone, as the
 * Comparison table does in the same case.
 */
import type { BvBill, ShadowBillResult } from '../types/billTypes';
import type { MapContext } from '../utils/comparisonRows';
import { effectiveExclusions, isSectionExcluded } from '../utils/calcExclusions';
import { showsEnvironmental } from '../utils/environmental';
import { effectiveInvoiceResult } from './effectiveInvoice';
import { reportComparison, type ReportSectionRow } from './comparisonSections';
import { classifyVariance, type VarianceKind } from './reportFormulas';

export type VerdictBucket = 'overcharge' | 'undercharge' | 'within_range';

export type BillVerdict = {
  /** Whole bill (internal screens): what we validate as calculated, everything
   *  else added back as billed. */
  expectedTotal: number;
  /** Whole bill as the retailer printed it. */
  invoiceTotal: number;
  /** The compared charges and their GST: what the client report states. */
  compared: { expected: number; invoice: number };
  /** compared.invoice − compared.expected: the difference in what we validate. */
  variance: number;
  /** Judged on the compared totals, so the report and the screens agree. */
  kind: VarianceKind;
  bucket: VerdictBucket;
};

const GST_RATE = 0.1;
const round2 = (n: number) => Math.round(n * 100) / 100;

export function bucketOf(kind: VarianceKind): VerdictBucket {
  if (kind === 'over') return 'overcharge';
  if (kind === 'under') return 'undercharge';
  return 'within_range';
}

/** GST on the compared charges: ours at 10%; the retailer's is what they
 *  printed less 10% of everything not compared. Null when GST is switched off. */
function comparedGst(input: {
  expectedSubtotal: number; retailerSubtotal: number; gstOff: boolean;
  invoiceSubtotal: number; invoiceGst: number;
}): { expected: number; invoice: number } | null {
  if (input.gstOff) return null;
  return {
    expected: round2(input.expectedSubtotal * GST_RATE),
    invoice: round2(input.invoiceGst - (input.invoiceSubtotal - input.retailerSubtotal) * GST_RATE),
  };
}

export function verdictFromCompared(input: {
  expectedSubtotal: number;
  retailerSubtotal: number;
  gstOff: boolean;
  invoiceSubtotal: number;
  invoiceGst: number;
  invoiceTotal: number;
}, tolerancePct?: number): BillVerdict {
  const gst = comparedGst(input);
  const compared = {
    expected: round2(input.expectedSubtotal + (gst?.expected ?? 0)),
    invoice: round2(input.retailerSubtotal + (gst?.invoice ?? 0)),
  };
  const variance = round2(compared.invoice - compared.expected);
  const invoiceTotal = round2(input.invoiceTotal);
  const kind = classifyVariance(compared.expected, compared.invoice, tolerancePct);
  return { expectedTotal: round2(invoiceTotal - variance), invoiceTotal, compared, variance, kind, bucket: bucketOf(kind) };
}

export type BillReport = {
  /** The compared sections. No pass-through row: add-backs are internal only. */
  sections: ReportSectionRow[];
  subtotal: { expected: number; invoice: number };
  /** Null when GST is switched off: the report then runs ex-GST. */
  gst: { expected: number; invoice: number } | null;
  total: { expected: number; invoice: number };
  verdict: BillVerdict;
};

/**
 * The client report's table: the compared charges only (decided 2026-10-07).
 * Pass-through charges and the add-back stay on the internal screens; the
 * client sees what we validated, with its subtotal, the GST on it and a total
 * that add up.
 */
export function billReport(bill: Partial<BvBill>, mapContext?: MapContext, tolerancePct?: number): BillReport | null {
  const sr = bill.shadow_result as ShadowBillResult | null | undefined;
  const ar = effectiveInvoiceResult(bill);
  if (!sr || !ar || !Array.isArray(sr.lines) || !Array.isArray(ar.lines)) return null;
  const exclusions = effectiveExclusions(bill as Pick<BvBill, 'site' | 'calc_exclusions'>);
  const { sections, totals: t } = reportComparison(sr, ar, exclusions, showsEnvironmental(bill.site), mapContext);
  const input = {
    expectedSubtotal: t.expectedSubtotal,
    retailerSubtotal: t.retailerSubtotal,
    gstOff: isSectionExcluded('gst', exclusions),
    invoiceSubtotal: ar.subtotal,
    invoiceGst: ar.gst,
    invoiceTotal: ar.total,
  };
  const verdict = verdictFromCompared(input, tolerancePct);
  return {
    sections: sections.filter((s) => s.key !== 'not_compared'),
    subtotal: { expected: t.expectedSubtotal, invoice: t.retailerSubtotal },
    gst: comparedGst(input),
    total: verdict.compared,
    verdict,
  };
}

/** The bill's verdict, or null when it has no expected invoice or no retailer invoice. */
export function billVerdict(bill: Partial<BvBill>, mapContext?: MapContext): BillVerdict | null {
  return billReport(bill, mapContext)?.verdict ?? null;
}
